import React from 'react';
import OverallFeedbackTextarea from './OverallFeedbackTextarea';

/**
 * FeedbackSubmitForm - Generisk feedback-formulär
 * Återanvändbar för estimat, offert, upphandling etc.
 *
 * @param {string} title - Huvudtitel (t.ex. "Vad tycker ni om estimatet?")
 * @param {string} description - Beskrivning under titeln
 * @param {string} overallFeedback - Värde för övergripande feedback
 * @param {function} onOverallFeedbackChange - Callback när feedback ändras
 * @param {function} onOverallFeedbackBlur - Callback vid blur (för aktivitetsloggning)
 * @param {boolean} showMeetingRequest - Om mötesbegäran ska visas (default: true)
 * @param {array} meetingSlots - Valda mötestider
 * @param {function} onOpenMeetingModal - Callback för att öppna tidsval-modal
 * @param {function} onSubmit - Callback när formuläret skickas
 * @param {string} submitButtonText - Text på skicka-knappen
 * @param {boolean} submitting - Om formuläret håller på att skickas
 * @param {boolean} requireMeetingSlots - Om minst 1 tidsslot krävs när möte begärs
 * @param {ReactNode} children - Custom innehåll (t.ex. tidsförslag från byggkonsult)
 * @param {string} meetingTitle - Titel för mötesbegäran
 * @param {string} meetingDescription - Beskrivning för mötesbegäran
 */
const FeedbackSubmitForm = ({
  title = 'Vad tycker ni om estimatet?',
  description = 'Vi vill gärna ha er feedback på estimatet.',
  overallFeedback,
  onOverallFeedbackChange,
  onOverallFeedbackBlur,
  // Prioriterade frågor från första-estimatmailet — visas ovanför Övergripande
  // feedback. Optional: om inte angivna, hoppar formuläret över dem.
  budgetFeedback,
  onBudgetFeedbackChange,
  onBudgetFeedbackBlur,
  prioriteringFeedback,
  onPrioriteringFeedbackChange,
  onPrioriteringFeedbackBlur,
  showMeetingRequest = true,
  meetingSlots = [],
  onOpenMeetingModal,
  onSubmit,
  submitButtonText = '💬 Skicka feedback',
  submitting = false,
  requireMeetingSlots = false,
  children,
  meetingTitle = 'Begär genomgång via Teams',
  meetingDescription = 'Vi bokar ett kort möte för att gå igenom estimatet och svara på frågor.',
}) => {
  const wantsMeeting = meetingSlots.length > 0;
  const isSubmitDisabled = submitting || (requireMeetingSlots && wantsMeeting && meetingSlots.length < 1);

  return (
    <div id="feedback-section" className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
      {/* Header */}
      <div className="p-6 border-b border-gray-200">
        <h2 className="text-lg font-semibold text-gray-800">{title}</h2>
        <p className="text-sm text-gray-600 mt-1">{description}</p>
      </div>

      <div className="p-6 space-y-6">
        {/* Teams-genomgång */}
        {showMeetingRequest && onOpenMeetingModal && (
          <div className="border border-gray-200 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-semibold text-gray-900">{meetingTitle}</h3>
                <p className="text-xs text-gray-500">{meetingDescription}</p>
              </div>
            </div>
            <div className="mt-3 pl-13">
              <button
                type="button"
                className="px-4 py-2 text-sm font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors inline-flex items-center gap-2"
                onClick={onOpenMeetingModal}
              >
                + Föreslå mötestider
              </button>
              {Array.isArray(meetingSlots) && meetingSlots.length > 0 && (
                <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-xs text-green-700 font-medium">Valda tider: {meetingSlots.join(', ')}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Prioriterade frågor från mailet — visas bara om change-handlers angivna */}
        {onBudgetFeedbackChange && (
          <div>
            <OverallFeedbackTextarea
              id="budgetFeedback"
              label="Matchar detta er önskade budget?"
              placeholder="Skriv ert svar här..."
              helpText="Skickas tillsammans med övrig feedback."
              value={budgetFeedback || ''}
              onChange={onBudgetFeedbackChange}
              onBlur={onBudgetFeedbackBlur}
              rows={3}
            />
          </div>
        )}
        {onPrioriteringFeedbackChange && (
          <div>
            <OverallFeedbackTextarea
              id="prioriteringFeedback"
              label="Är det någon del vi ska prioritera bort/ned/upp?"
              placeholder="Skriv ert svar här..."
              helpText="Skickas tillsammans med övrig feedback."
              value={prioriteringFeedback || ''}
              onChange={onPrioriteringFeedbackChange}
              onBlur={onPrioriteringFeedbackBlur}
              rows={3}
            />
          </div>
        )}

        {/* Övergripande feedback */}
        <div>
          <OverallFeedbackTextarea
            value={overallFeedback}
            onChange={onOverallFeedbackChange}
            onBlur={onOverallFeedbackBlur}
          />
        </div>

        {/* Custom innehåll (t.ex. byggkonsultens tidsförslag) */}
        {children}

        {/* Submit-knapp */}
        <div className="flex justify-start pt-2">
          <button
            onClick={onSubmit}
            disabled={isSubmitDisabled}
            className="bg-leadme-pink hover:bg-leadme-pink-dark text-white px-8 py-3 rounded-lg text-base font-medium transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed shadow-sm"
          >
            {submitting ? 'Skickar...' : submitButtonText}
          </button>
        </div>

        {/* Valideringsmeddelande */}
        {requireMeetingSlots && wantsMeeting && meetingSlots.length < 1 && (
          <div className="text-xs text-red-600 text-center">
            Välj minst en mötestid eller avmarkera mötesförfrågan.
          </div>
        )}
      </div>
    </div>
  );
};

export default FeedbackSubmitForm;

